import { getType } from "../tower_model/lockersHelpers";
import { debug_log } from "../../ra-lb-tools/utils/debugLog";
import { Line, Rect, Text } from "react-konva";
import { AccessRender, LowRender, StatusRender } from "./icons";
import { styleSchema } from "./style";
import { Ruler } from "./Ruler";
import { MyRect } from "./MyRect";
import { shieldThreshold } from "./options";
import { RGBA } from "./rgba";
import { HPOS, VPOS } from "./coord";

const manufacturerLogoPath = "/admin/static/img/";

const manufacturerLogo = {
  1: "harbor-brand.png",
  2: "luxer-one-logo.svg",
  3: "american-locker-logo.svg",
  4: "ottonomy-logo.png",
};

const getShield = (pId) => Math.ceil(pId / shieldThreshold);
const getShieldLabel = (pId) => getShield(pId) + "-";
const getPId = (pId, withShield) =>
  withShield ? ((pId - 1) % shieldThreshold) + 1 : pId;

const getPIdLabel = (pId, withShield) =>
  "[#" +
  (withShield ? getShieldLabel(pId) : "") +
  getPId(pId, withShield) +
  "]";

const getNameLabel = (name) =>
  name
    .replace(/extra/gi, "xtra")
    .split(" ")
    .map((str) => str.toUpperCase()[0])
    .join("");

const PrettyBorder = ({ index, renderProps, lockerStyleSchema }) => {
  return (
    <>
      <Line
        key={index + "border1"}
        points={[
          renderProps.x,
          renderProps.y,
          renderProps.x + renderProps.width,
          renderProps.y,
        ]}
        {...lockerStyleSchema.prettyBorder.h1}
      />
      <Line
        key={index + "border2"}
        points={[
          renderProps.x + 10,
          renderProps.y,
          renderProps.x + renderProps.width - 10,
          renderProps.y,
        ]}
        {...lockerStyleSchema.prettyBorder.h2}
      />
      <Line
        key={index + "border3"}
        points={[
          renderProps.x,
          renderProps.y + 3,
          renderProps.x + renderProps.width,
          renderProps.y + 3,
        ]}
        {...lockerStyleSchema.prettyBorder.h3}
      />
      <Line
        key={index + "border4"}
        points={[
          renderProps.x + renderProps.width - 3,
          renderProps.y,
          renderProps.x + renderProps.width - 3,
          renderProps.y + renderProps.height,
        ]}
        {...lockerStyleSchema.prettyBorder.v1}
      />
    </>
  );
};

export const LockerRender = ({
  x,
  y,
  baseLockerSize,
  spec,
  types,
  index,
  highlightList,
  highlight_key,
  onMouseEnter,
  onMouseLeave,
  lockerNameGenerator,
  onClick,
  options,
  towerModel,
}) => {
  const lockerType = getType(spec, types);
  if (!lockerType) {
    debug_log(`locker type not found, phys id: ${spec.physicalId}`);
    return null;
  }

  const renderProps = {
    width: (spec.width || lockerType.relWidth) * baseLockerSize.width,
    height: (spec.height || lockerType.relHeight) * baseLockerSize.height,
    x:
      x +
      (spec.x || spec.colPosition - 1 + spec.offsetPosition) *
        baseLockerSize.width,
    y: y + (spec.y || spec.rowPosition - 1) * baseLockerSize.height,
  };

  if (Object.values(renderProps).some((value) => isNaN(value))) {
    debug_log(`LockerRender: NaN value found phys id: ${spec.physicalId}`);
    return null;
  }

  const name =
    spec.name || (lockerNameGenerator ? lockerNameGenerator.next().value : "");

  const label =
    spec.label ||
    [
      options.type ? getNameLabel(lockerType.name) : "",
      options.name ? name : "",
      options.port ? getPIdLabel(spec.physicalId, options.shield) : "",
    ].join(" ");

  const lockerStyleSchema = styleSchema.locker;
  let fillByType =
    lockerStyleSchema.backgroundByAccess[spec.accessModeId] ||
    lockerStyleSchema.backgroundByAccess.default;

  if (spec.statusId == 4) {
    fillByType = lockerStyleSchema.oos;
  }

  let fill = spec.fill ? { fill: spec.fill } : fillByType;
  if (options.bg_src) {
    const opacityOffset =
      spec.accessModeId == 1
        ? lockerStyleSchema.opacityForDefaultBgImg
        : lockerStyleSchema.opacityForBgImg;
    fill = {
      ...fill,
      fill: RGBA.opacityStep(fill.fill, opacityOffset),
    };
  }

  let style = lockerStyleSchema.default;

  if (highlightList.includes(spec[highlight_key])) {
    style = lockerStyleSchema.highlight;
    if (options.bg_src) {
      fill = { ...fill, fill: RGBA.opacityStep(fill.fill, 1) };
    } else {
      fill = { ...fill, fill: RGBA.brightStep(fill.fill, -20) };
    }
  } else if (
    options.highlight_shield &&
    options.highlight_shield != getShield(spec.physicalId)
  ) {
    style = lockerStyleSchema.activeShield;
  }

  const textOffset = {
    x: 0,
    y: 0,
  };

  let labelVPos = VPOS.CENTER;
  if (spec.physicalId == options.branded) {
    fill = {
      ...fill,
      backgroundImage: {
        src: `${manufacturerLogoPath}${
          manufacturerLogo[towerModel?.manufacturer || 1]
        }`,
        fitMode: "contain",
        padding: 12,
        opacity: 1,
      },
    };
    labelVPos = VPOS.TOP;
    textOffset.y = 3;
  }

  return [
    <MyRect
      key={index}
      opacity={style.opacity}
      stroke={style.strokeColor}
      strokeWidth={style.strokeWidth}
      {...fill}
      coords={renderProps}
    >
      <Text
        fill={style.textColor}
        opacity={style.textOpacity ? style.textOpacity : null}
        label={label}
        vpos={labelVPos}
        hpos={HPOS.MIDDLE}
        offset={textOffset}
      />
    </MyRect>,
    <PrettyBorder
      key={index + "b"}
      index={index}
      renderProps={renderProps}
      lockerStyleSchema={lockerStyleSchema}
    />,
    options.low && (
      <LowRender
        key={index + "low"}
        spec={spec}
        style={style}
        renderProps={renderProps}
      />
    ),
    options.access && (
      <AccessRender
        key={index + "acc"}
        spec={spec}
        style={style}
        renderProps={renderProps}
      />
    ),
    options.status && spec.statusId && (
      <StatusRender
        key={index + "st"}
        spec={spec}
        style={style}
        renderProps={renderProps}
      />
    ),
    <Rect
      key={index + "event"}
      fill={"transparent"}
      stroke={"transparent"}
      x={renderProps.x}
      y={renderProps.y}
      width={renderProps.width}
      height={renderProps.height}
      onMouseEnter={() => onMouseEnter && onMouseEnter(spec)}
      onMouseLeave={() => onMouseLeave && onMouseLeave(spec)}
      onClick={() => onClick && onClick(spec)}
    />,
    ["locker", "all"].includes(options.ruler) && (
      <Ruler
        frame={{
          pos: {
            x: renderProps.x,
            y: renderProps.y,
          },
          size: {
            width: renderProps.width,
            height: renderProps.height,
          },
        }}
        numColumns={spec.width || lockerType.relWidth}
        numRows={spec.height || lockerType.relHeight}
        inside={true}
        options={options}
      />
    ),
  ];
};

LockerRender.defaultProps = {
  highlightList: [],
  highlight_key: "physicalId",
};
